import { HeaderCheckoutElementDataType } from '@/components/sections/builder/elements/header/header-checkout/type';
import CartButton from '@/components/sections/builder/elements/header/header/cart-button';
import HeaderLogo from '@/components/sections/builder/elements/header/header/header-logo';
import { ElementProps } from '@/components/sections/builder/types';
import { useIsMobile } from '@/hooks/use-mobile';
import useScrollDirection from '@/hooks/useScrollDirection';
import { cn } from '@/lib/utils';
import { ThemeType } from '@/stores/builder';
import React from 'react';

export default function ElementContentHeaderCheckout({ element, preview, theme }: ElementProps) {
  const data = element.data as HeaderCheckoutElementDataType;

  const isMobile = useIsMobile();
  const scrollDirection = useScrollDirection();

  const [headerFixed, setHeaderFixed] = React.useState<boolean>(false);
  const [headerStatic, setHeaderStatic] = React.useState<boolean>(true);
  const [logoW, setLogoW] = React.useState<string>('100px');
  const [headerHeight, setHeaderHeight] = React.useState<number>(0);

  const headerRef = React.useRef<HTMLElement | null>(null);
  const logoRef = React.useRef<HTMLDivElement | null>(null);

  const stickyMode = (data?.stickyHeader as string) ?? 'none';
  const isStickyMode = stickyMode === 'on-scroll-up' || stickyMode === 'always';

  React.useLayoutEffect(() => {
    const el = logoRef.current;
    if (!el) return;
    const w = el.getBoundingClientRect().width;
    setLogoW(`${Math.ceil(w) + 24}px`);
  }, [data?.logoUrl]);

  React.useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const measure = () =>
      setHeaderHeight(Math.ceil(headerRef.current?.getBoundingClientRect().height || 0));
    measure();

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h =
          entry.borderBoxSize?.[0]?.blockSize ??
          entry.contentBoxSize?.[0]?.blockSize ??
          entry.contentRect.height ??
          el.offsetHeight;
        setHeaderHeight(Math.ceil(h || 0));
      }
    });
    ro.observe(el);

    const onFont = () => measure();
    document.fonts?.addEventListener?.('loadingdone', onFont);

    return () => {
      ro.disconnect();
      document.fonts?.removeEventListener?.('loadingdone', onFont);
    };
  }, []);

  React.useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    setHeaderHeight(Math.ceil(el.offsetHeight || 0));
  }, [
    isMobile,
    data?.padding?.top,
    data?.padding?.bottom,
    data?.padding?.left,
    data?.padding?.right,
  ]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const el = headerRef.current;
    const wrapperHeader =
      (document.getElementById('wrapper-header_checkout')?.offsetHeight as number) || 0;

    const spacing = Number(data?.spacing || 0);

    if (!isStickyMode) {
      setHeaderFixed(false);
      setHeaderStatic(true);
      return;
    }

    const stickyTop = el ? Number((el as any).offsetTop || 0) : 0;

    const onScroll = () => {
      const y = window.scrollY;

      setHeaderFixed(stickyMode === 'on-scroll-up' && y > stickyTop + wrapperHeader - spacing);

      setHeaderStatic(y <= stickyTop + (wrapperHeader - (el?.offsetHeight || 0)) - spacing);
    };

    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [data?.spacing, isStickyMode, stickyMode]);

  const getLayoutClasses = () => {
    const position = isMobile ? data.mobileLogoPosition : data.logoPosition;
    switch (position) {
      case 'center':
        return {
          container: 'relative flex items-center justify-center',
          logo: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center space-x-2 z-20',
          actions: 'absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 z-20',
        };
      case 'left':
      default:
        return {
          container: 'relative flex items-center justify-between',
          logo: 'absolute left-4 top-1/2 -translate-y-1/2 flex items-center space-x-2 z-20',
          actions: 'absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 z-20',
        };
    }
  };

  const layoutClasses = getLayoutClasses();
  const spacerActive =
    isStickyMode &&
    (headerFixed || ((scrollDirection === 'up' || stickyMode === 'always') && !headerStatic));

  return (
    <>
      {/* Navigation */}
      <header
        ref={headerRef}
        id='header-sticky'
        className={cn(
          'inset-x-0 z-40 bg-white text-sm',
          isStickyMode && headerFixed && 'offset-top fixed duration-300',
          isStickyMode &&
            (scrollDirection === 'up' || stickyMode === 'always') &&
            !headerStatic &&
            'fixed !top-0 duration-300',
          'items-center justify-start md:justify-between',
          data.isShowSeparator && 'border-b',
          isMobile ? 'px-6 lg:!px-4' : 'padding',
          'font-scale header-full-width min-h-[3.5rem] md:min-h-[3.5rem]'
        )}
      >
        <div className='theme_layout_page_width mx-auto h-full'>
          <div
            className={cn(layoutClasses.container, 'min-h-[3.5rem] md:min-h-[3.5rem]')}
            style={
              {
                '--logo-w': isMobile ? 50 : logoW,
              } as React.CSSProperties
            }
          >
            {/* Logo */}
            <div ref={logoRef} className={layoutClasses.logo}>
              <HeaderLogo
                {...data}
                logoWidth={(data.logoWidth ?? 100) > 100 ? data.logoWidth : 100}
              />
            </div>

            {/* Actions */}
            <div className={cn(layoutClasses.actions, 'lg:gap-x-4')}>
              {data.isShowCart && (
                <CartButton
                  host={typeof window !== 'undefined' ? window.location.host : ''}
                  preview={!!preview}
                  theme={theme as ThemeType}
                />
              )}
            </div>
          </div>
        </div>
      </header>

      <div style={{ height: spacerActive ? headerHeight : 0 }} />

      <style jsx>
        {`
          @media (min-width: 768px) {
            .padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }
          .offset-top {
            top: -${headerHeight}px;
          }
          .header-full-width {
            max-width: unset;
            width: 100%;
          }
        `}
      </style>
    </>
  );
}
